import { useEffect, useState, useRef } from 'react';

interface UseTabVisibilityOptions {
  tabId: string;
  onTabActivated?: () => void;
  onTabDeactivated?: () => void;
}

export const useTabVisibility = ({ 
  tabId, 
  onTabActivated, 
  onTabDeactivated 
}: UseTabVisibilityOptions) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenActivated, setHasBeenActivated] = useState(false);
  const observerRef = useRef<MutationObserver | null>(null);

  useEffect(() => {
    const checkTabVisibility = () => {
      const tabElement = document.querySelector(`#${tabId}`);
      if (tabElement) {
        const isCurrentlyVisible = tabElement.classList.contains('show') && 
                                 tabElement.classList.contains('active');
        
        if (isCurrentlyVisible && !isVisible) {
          setIsVisible(true);
          setHasBeenActivated(true);
          onTabActivated?.();
        } else if (!isCurrentlyVisible && isVisible) {
          setIsVisible(false);
          onTabDeactivated?.();
        }
      }
    };

    // Check immediately when component mounts
    checkTabVisibility();

    // Set up a MutationObserver to watch for tab changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          checkTabVisibility();
        }
      });
    });

    // Observe the tab for class changes
    const tabElement = document.querySelector(`#${tabId}`);
    if (tabElement) {
      observer.observe(tabElement, { attributes: true });
      observerRef.current = observer;
    }

    // Also listen for Bootstrap tab events
    const handleTabShown = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target && target.id === tabId) {
        checkTabVisibility();
      }
    };

    document.addEventListener('shown.bs.tab', handleTabShown);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      document.removeEventListener('shown.bs.tab', handleTabShown);
    };
  }, [tabId, isVisible, onTabActivated, onTabDeactivated]);

  return {
    isVisible,
    hasBeenActivated,
    checkTabVisibility: () => {
      const tabElement = document.querySelector(`#${tabId}`);
      return tabElement?.classList.contains('show') && 
             tabElement?.classList.contains('active');
    }
  };
}; 